<template>
  <div class="dbe">
    <!-- Timeline: day track + draggable blocks + hour labels -->
    <div
      ref="container"
      class="dbe-canvas"
    >
      <div class="dbe-track" />

      <template v-if="value.enabled">
        <div
          v-for="bv in blockViews"
          :key="bv.block.id"
          class="dbe-block"
          :style="{
            left: bv.left + '%',
            width: bv.width + '%',
            background: bv.col.bg,
            border: '1.5px solid ' + bv.col.border,
            cursor: bv.isDragging ? 'grabbing' : 'grab',
            transition: drag ? 'none' : 'left 0.1s,width 0.1s',
          }"
          @pointerdown="bDown($event, bv.block)"
          @pointerup="bUp"
        >
          <div
            class="dbe-handle dbe-handle--l"
            @pointerdown="hDown($event, bv.block.id, 'left')"
          >
            <div
              class="dbe-grip"
              :style="{ background: bv.col.handle }"
            />
          </div>
          <div
            class="dbe-handle dbe-handle--r"
            @pointerdown="hDown($event, bv.block.id, 'right')"
          >
            <div
              class="dbe-grip"
              :style="{ background: bv.col.handle }"
            />
          </div>
          <span
            class="dbe-block-label"
            :style="{ color: bv.col.text }"
          >{{ idxSlot(bv.block.from) }}–{{ idxSlot(bv.block.to) }}</span>
        </div>
      </template>

      <div class="dbe-labels">
        <span
          v-for="o in hourLabels"
          :key="o.h"
          class="dbe-hour"
          :style="{ left: o.x + '%' }"
        >{{ o.h }}</span>
      </div>
    </div>

    <template v-if="value.enabled">
      <!-- Live coverage of popular start times -->
      <div class="dbe-chips">
        <span class="dbe-chips-label">Popular start times:</span>
        <span
          v-for="c in cov"
          :key="c.h"
          class="dbe-chip"
          :class="c.status === 'ok' ? 'ok' : 'bad'"
        >{{ c.status === 'ok' ? '✓' : '✕' }} {{ fmtHour(c.h) }}</span>
      </div>

      <!-- Block rows -->
      <div class="dbe-rows">
        <div
          v-for="bv in blockViews"
          :key="bv.block.id"
          class="dbe-row"
          :style="{ borderLeft: '4px solid ' + bv.col.border }"
        >
          <div class="dbe-row-times">
            <div class="dbe-time-line">
              <select
                class="dbe-sel"
                aria-label="Block start time"
                :value="idxSlot(bv.block.from)"
                @change="upTime(bv.block.id, 'from', $event.target.value)"
              >
                <option
                  v-for="o in timeOptions"
                  :key="o"
                  :value="o"
                >{{ o }}</option>
              </select>
              <span class="dbe-dash">–</span>
              <select
                class="dbe-sel"
                aria-label="Block end time"
                :value="idxSlot(bv.block.to)"
                @change="upTime(bv.block.id, 'to', $event.target.value)"
              >
                <option
                  v-for="o in timeOptions"
                  :key="o"
                  :value="o"
                >{{ o }}</option>
              </select>
            </div>
            <span
              v-if="bv.tooShort"
              class="dbe-note warn"
              title="Shorter than one date. Widen the block or reduce the date duration."
            >too short for a {{ duration }}-min date</span>
            <span
              v-else
              class="dbe-note"
              title="Worst case: the latest a couple could arrive and still finish before the block ends."
            >latest possible arrival <strong>{{ bv.lastArr }}</strong></span>
          </div>

          <div class="dbe-row-right">
            <div class="dbe-max-label">
              <span class="dbe-max-l1">Max couples</span>
              <span class="dbe-max-l2">seated at once</span>
            </div>

            <div class="dbe-stepper">
              <button
                type="button"
                class="dbe-step"
                title="Fewer"
                aria-label="Fewer couples"
                @click="decMax(bv.block)"
              >−</button>
              <input
                type="text"
                inputmode="numeric"
                class="dbe-max-input"
                aria-label="Max couples seated at once"
                :value="bv.block.max"
                @input="onMaxInput(bv.block.id, $event)"
              >
              <button
                type="button"
                class="dbe-step"
                title="More"
                aria-label="More couples"
                @click="incMax(bv.block)"
              >+</button>
            </div>

            <button
              v-if="value.blocks.length > 1"
              type="button"
              class="dbe-rm"
              title="Remove this interval"
              aria-label="Remove this interval"
              @click="rmBlock(bv.block.id)"
            >✕</button>
          </div>
        </div>

        <button
          v-if="canAdd"
          type="button"
          class="dbe-add"
          @click="addBlock"
        >+ Add another time interval</button>
        <div
          v-else
          class="dbe-full"
        >This day is fully covered. There's no {{ duration }}-min gap left for another interval. Shorten an interval above to make room.</div>
      </div>

      <!-- Nudge banner -->
      <div
        class="dbe-nudge"
        :style="{ background: nudge.style.bg, border: '1px solid ' + nudge.style.bd }"
      >
        <div
          class="dbe-nudge-dot"
          :style="{ background: nudge.style.dot }"
        >{{ nudge.style.glyph }}</div>
        <div
          class="dbe-nudge-text"
          :style="{ color: nudge.style.fg }"
        >{{ nudge.msg }}</div>
        <button
          v-if="nudge.level !== 'ok'"
          type="button"
          class="dbe-nudge-btn"
          :style="{ border: '1px solid ' + nudge.style.bd, color: nudge.style.fg }"
          title="Reset this day to Breeze's recommended hours"
          @click="useRecommended"
        >Use recommended</button>
      </div>
    </template>
  </div>
</template>

<script>
import { colors } from '@/design-system'
import {
  BLOCK_COLORS, SLOTS15, slotIdx, idxSlot, recommendedBlock,
} from './capacity-helpers'

const START_HOUR = 10

export default {
  name: 'DayBlockEditor',
  props: {
    // The day's capacity: { enabled: Boolean, blocks: [{ id, from, to, max }] }
    value: { type: Object, required: true },
    // { key, label, dayName, wk }
    day: { type: Object, required: true },
    // Date duration in minutes; the arrival cutoff is derived from this.
    duration: { type: Number, default: 90 },
  },
  data() {
    return { drag: null }
  },
  computed: {
    durSlots() {
      return Math.ceil(this.duration / 15)
    },
    LAST() {
      return SLOTS15.length
    },
    winStart() {
      return (START_HOUR - 8) * 4
    },
    winSlots() {
      return this.LAST - this.winStart
    },
    timeOptions() {
      const out = []
      for (let ti = this.winStart; ti <= this.LAST; ti += 1) out.push(idxSlot(ti))
      return out
    },
    hourLabels() {
      const out = []
      for (let hh = START_HOUR; hh <= 23; hh += 1) out.push({ h: hh, x: this.idxToX((hh - 8) * 4) })
      return out
    },
    blockViews() {
      return this.value.blocks.map((block, i) => {
        const col = BLOCK_COLORS[i % 3]
        const left = this.idxToX(block.from)
        const right = this.idxToX(block.to)
        return {
          block,
          i,
          col,
          left,
          width: right - left,
          isDragging: !!(this.drag && this.drag.blockId === block.id && this.drag.edge === 'move'),
          lastArr: idxSlot(Math.max(block.from, block.to - this.durSlots)),
          // A block must be longer than one date to leave a bookable window (openUntil > openFrom).
          tooShort: (block.to - block.from) <= this.durSlots,
        }
      })
    },
    // Coverage of the popular start times for this day.
    cov() {
      const ds = this.durSlots
      const slotHours = this.day.wk ? [15, 18, 19, 20] : [18, 19, 20]
      return slotHours.map(hr => {
        const T = (hr - 8) * 4
        const fits = this.value.blocks.some(b => b.from <= T && T + ds <= b.to)
        const insideLate = !fits && this.value.blocks.some(b => b.from <= T && T < b.to)
        const beforeAll = this.value.blocks.length > 0 && this.value.blocks.every(b => T < b.from)
        return { h: hr, T, status: this.covStatus(fits, insideLate, beforeAll) }
      })
    },
    canAdd() {
      const ds = this.durSlots
      const sb = this.value.blocks.slice().sort((a, b) => a.from - b.from)
      let cur = this.winStart
      for (let k = 0; k < sb.length; k += 1) {
        if (sb[k].from - cur >= ds) return true
        cur = sb[k].to
      }
      return this.LAST - cur >= ds
    },
    nudge() {
      const ds = this.durSlots
      const slotHours = this.day.wk ? [15, 18, 19, 20] : [18, 19, 20]
      const servable = this.cov.filter(c => c.status === 'ok')
      const missedEarly = this.cov.filter(c => c.status === 'early')
      const missedLate = this.cov.filter(c => c.status === 'late' || c.status === 'gap')
      const anyTooShort = this.value.blocks.some(b => b.to - b.from <= ds)
      let level
      let msg
      if (servable.length === 0) {
        level = 'error'
        msg = anyTooShort
          ? `These hours are shorter than a ${this.duration}-minute date, so Breeze can’t seat anyone. Make the block at least ${this.duration} minutes long.`
          : `These hours don’t leave room for a full ${this.duration}-minute date at any popular start time. Try opening on the hour so couples can book ${this.fmtHour(slotHours[0])} or ${this.fmtHour(slotHours[1])}.`
      } else {
        const tail = []
        if (missedEarly.length) tail.push(`you’re missing ${this.fmtList(missedEarly.map(c => this.fmtHour(c.h)))}, because your window opens too late`)
        if (missedLate.length) tail.push(`${this.fmtList(missedLate.map(c => this.fmtHour(c.h)))} won’t fit, because a ${this.duration}-min date would run past your block’s end`)
        level = tail.length ? 'warn' : 'ok'
        msg = `Breeze can seat couples arriving at ${this.fmtList(servable.map(c => this.fmtHour(c.h)))}${tail.length ? `. But ${tail.join('; and ')}.` : '. That covers every popular slot.'}`
      }
      return { level, msg, style: this.nudgeStyle(level) }
    },
  },
  mounted() {
    this.boundMove = this.onMove.bind(this)
    this.boundUp = this.onUp.bind(this)
    window.addEventListener('pointermove', this.boundMove)
    window.addEventListener('pointerup', this.boundUp)
  },
  beforeDestroy() {
    window.removeEventListener('pointermove', this.boundMove)
    window.removeEventListener('pointerup', this.boundUp)
    if (this.lpTimer) clearTimeout(this.lpTimer)
  },
  methods: {
    idxSlot,
    fmtHour(hr) {
      return `${String(hr).padStart(2, '0')}:00`
    },
    fmtList(arr) {
      if (arr.length <= 1) return arr[0] || ''
      return `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`
    },
    covStatus(fits, insideLate, beforeAll) {
      if (fits) return 'ok'
      if (insideLate) return 'late'
      if (beforeAll) return 'early'
      return 'gap'
    },
    nudgeStyle(level) {
      if (level === 'error') {
        return {
          bg: colors.dangerLight, bd: `${colors.danger}33`, dot: colors.danger, fg: '#7A2E2E', glyph: '!',
        }
      }
      if (level === 'warn') {
        return {
          bg: '#FCF1E4', bd: '#F0CFA0', dot: '#D9892B', fg: '#6B4A22', glyph: '!',
        }
      }
      return {
        bg: `${colors.success}12`, bd: `${colors.success}2E`, dot: colors.success, fg: '#1F5E3A', glyph: '✓',
      }
    },
    idxToX(i) {
      return ((i - this.winStart) / this.winSlots) * 100
    },
    update(nd) {
      this.$emit('input', nd)
    },
    // No-overlap guard: blocks may sit flush but never overlap.
    boundsFor(blocks, blk) {
      const winStart = this.winStart
      let lo = winStart
      let hi = this.LAST
      blocks.forEach(o => {
        if (o.id === blk.id) return
        if (o.to <= blk.from) lo = Math.max(lo, o.to)
        else if (o.from >= blk.to) hi = Math.min(hi, o.from)
        else if (o.from < blk.from) lo = Math.max(lo, o.to)
        else hi = Math.min(hi, o.from)
      })
      return [lo, hi]
    },
    onMove(e) {
      if (!this.drag || !this.$refs.container) return
      const rect = this.$refs.container.getBoundingClientRect()
      const LAST = this.LAST
      const ds = this.durSlots
      const winStart = this.winStart
      const winSlots = this.winSlots
      const xPct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100))
      const idx = Math.max(winStart, Math.min(LAST, Math.round(winStart + (xPct / 100) * winSlots)))
      const dg = this.drag
      const blocks = this.value.blocks.map(b => {
        if (b.id !== dg.blockId) return b
        const [lo, hi] = this.boundsFor(this.value.blocks, b)
        if (dg.edge === 'left') return { ...b, from: Math.max(lo, Math.min(idx, b.to - ds)) }
        if (dg.edge === 'right') return { ...b, to: Math.max(b.from + ds, Math.min(idx, LAST, hi)) }
        if (dg.edge === 'move') {
          const w = dg.bw
          const nf = Math.max(lo, Math.min(Math.min(LAST, hi) - w, idx - dg.go))
          return { ...b, from: nf, to: nf + w }
        }
        return b
      })
      this.update({ ...this.value, blocks })
    },
    onUp() {
      this.drag = null
    },
    hDown(e, id, edge) {
      e.preventDefault()
      e.stopPropagation()
      this.drag = { blockId: id, edge }
    },
    bDown(e, block) {
      const rect = this.$refs.container.getBoundingClientRect()
      const xPct = ((e.clientX - rect.left) / rect.width) * 100
      // Grab offset must be in the same window space onMove uses (winStart..LAST), not the
      // full 08:00 grid, or the block jumps on the first pointermove.
      const idx = Math.round(this.winStart + (xPct / 100) * this.winSlots)
      this.lpTimer = setTimeout(() => {
        this.drag = {
          blockId: block.id, edge: 'move', bw: block.to - block.from, go: idx - block.from,
        }
      }, 180)
    },
    bUp() {
      if (this.lpTimer) clearTimeout(this.lpTimer)
    },
    addBlock() {
      const sorted = this.value.blocks.slice().sort((a, b) => a.from - b.from)
      const LAST = this.LAST
      const ds = this.durSlots
      const winStart = this.winStart
      let f = slotIdx('11:00')
      let t = slotIdx('22:30')
      if (sorted.length > 0) {
        const last = sorted[sorted.length - 1]
        if (LAST - last.to >= ds) {
          f = last.to
          t = Math.min(f + 12, LAST)
        } else {
          f = -1
          let cursor = winStart
          for (let i = 0; i < sorted.length; i += 1) {
            const b = sorted[i]
            if (b.from - cursor >= ds) {
              f = cursor
              t = Math.min(f + 12, b.from)
              break
            }
            cursor = b.to
          }
          if (f < 0) return
        }
      }
      this.update({
        ...this.value,
        blocks: this.value.blocks.concat([{ ...recommendedBlock(), from: f, to: t }]),
      })
    },
    rmBlock(id) {
      this.update({ ...this.value, blocks: this.value.blocks.filter(b => b.id !== id) })
    },
    upMax(id, v) {
      this.update({ ...this.value, blocks: this.value.blocks.map(b => (b.id === id ? { ...b, max: v } : b)) })
    },
    decMax(block) {
      this.upMax(block.id, Math.max(1, block.max - 1))
    },
    incMax(block) {
      this.upMax(block.id, Math.min(60, block.max + 1))
    },
    onMaxInput(id, e) {
      const n = parseInt((e.target.value || '').replace(/\D/g, ''), 10) || 1
      this.upMax(id, Math.min(60, Math.max(1, n)))
    },
    upTime(id, field, slot) {
      const idx = slotIdx(slot)
      const ds = this.durSlots
      const LAST = this.LAST
      this.update({
        ...this.value,
        blocks: this.value.blocks.map(b => {
          if (b.id !== id) return b
          const [lo, hi] = this.boundsFor(this.value.blocks, b)
          return field === 'from'
            ? { ...b, from: Math.max(lo, Math.min(idx, b.to - ds)) }
            : { ...b, to: Math.max(b.from + ds, Math.min(idx, LAST, hi)) }
        }),
      })
    },
    useRecommended() {
      this.update({ ...this.value, blocks: [recommendedBlock()] })
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/tokens';

.dbe {
  margin-bottom: 2px;
}

.dbe-canvas {
  position: relative;
  height: 56px;
  user-select: none;
  touch-action: none;
}

.dbe-track {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 22px;
  height: 32px;
  background: rgba($c-text-ter, 0.07);
  border-radius: 8px;
}

.dbe-block {
  position: absolute;
  bottom: 22px;
  height: 32px;
  border-radius: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dbe-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 11px;
  z-index: 3;
  display: flex;
  align-items: center;
  cursor: ew-resize;

  &--l {
    left: -4px;

    .dbe-grip { margin-left: 2px; }
  }

  &--r {
    right: -4px;
    justify-content: flex-end;

    .dbe-grip { margin-right: 2px; }
  }
}

.dbe-grip {
  width: 4px;
  height: 18px;
  border-radius: 2px;
}

.dbe-block-label {
  font-size: 12px;
  font-weight: 700;
  pointer-events: none;
}

.dbe-labels {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 18px;
}

.dbe-hour {
  position: absolute;
  font-size: 11px;
  color: $c-text-ter;
  transform: translateX(-50%);
}

.dbe-chips {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.dbe-chips-label {
  font-size: 11.5px;
  color: $c-text-sec;
  margin-right: 2px;
  font-weight: 500;
}

.dbe-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;

  &.ok {
    background: $c-success-light;
    color: #1f7a45;
    border: 1px solid rgba($c-success, 0.25);
  }

  &.bad {
    background: $c-danger-light;
    color: $c-danger;
    border: 1px solid rgba($c-danger, 0.19);
  }
}

.dbe-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
}

.dbe-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: $c-white;
  padding: 12px 16px;
  border-radius: 11px;
  border: 1px solid $c-border-light;
}

.dbe-row-times {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.dbe-time-line {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dbe-sel {
  border: none;
  background: transparent;
  font-size: 16px;
  font-weight: 700;
  color: $c-text;
  font-family: inherit;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 14px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M3 5l3 3 3-3' fill='none' stroke='%23B5A5AE' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right center;
  background-size: 10px;
}

.dbe-dash {
  color: $c-text-ter;
  font-size: 16px;
}

.dbe-note {
  font-size: 11.5px;
  color: $c-text-sec;

  strong { color: $c-text; }

  &.warn {
    color: $c-danger;
    font-weight: 600;
  }
}

.dbe-row-right {
  display: flex;
  align-items: center;
  gap: 11px;
  flex-shrink: 0;
}

.dbe-max-label {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  text-align: right;
}

.dbe-max-l1 {
  color: $c-text;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.dbe-max-l2 {
  color: $c-text-sec;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.dbe-stepper {
  display: flex;
  align-items: center;
  border: 1.5px solid $c-border;
  border-radius: 9px;
  overflow: hidden;
}

.dbe-step {
  width: 36px;
  height: 38px;
  border: none;
  background: $c-bg;
  color: $c-wine;
  font-size: 20px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.dbe-max-input {
  width: 46px;
  padding: 7px 0;
  border: none;
  border-left: 1px solid $c-border-light;
  border-right: 1px solid $c-border-light;
  font-size: 20px;
  font-weight: 700;
  font-family: inherit;
  color: $c-wine;
  background: $c-white;
  text-align: center;
  outline: none;
}

.dbe-rm {
  background: none;
  border: none;
  cursor: pointer;
  color: $c-text-ter;
  font-size: 17px;
  padding: 0 2px;
}

.dbe-add {
  background: rgba($c-orange, 0.05);
  border: 1.5px dashed rgba($c-orange, 0.4);
  border-radius: 10px;
  padding: 12px;
  font-size: 13.5px;
  font-weight: 600;
  color: $c-orange;
  cursor: pointer;
  font-family: inherit;
  width: 100%;
}

.dbe-full {
  background: $c-bg;
  border: 1px dashed $c-border;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12px;
  line-height: 1.5;
  color: $c-text-sec;
  text-align: center;
}

.dbe-nudge {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 8px;
  border-radius: 9px;
  padding: 9px 12px;
}

.dbe-nudge-dot {
  width: 17px;
  height: 17px;
  border-radius: 999px;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.dbe-nudge-text {
  font-size: 12px;
  line-height: 1.5;
  flex: 1;
}

.dbe-nudge-btn {
  flex-shrink: 0;
  background: #ffffff;
  border-radius: 7px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
}
</style>
